import React, { useContext, useState, useEffect } from "react";
import { ConversationsContext } from "../context/ConversationsContext";
import { Sidebar } from "../partials/Sidebar";
import { Header } from "../partials/Header";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Loading from "../components/Loading";
import { motion, AnimatePresence } from "framer-motion";

export const Layout = ({ children }) => {
  const { cId, jId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Accessing context
  const { conversations, loading, currentConversationInfo } = useContext(ConversationsContext);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const changeConversation = (cId, jId) => {
    const targetPath = `/chat/${cId}/${jId}`;
    if (location.pathname !== targetPath) {
      navigate(targetPath);
    }
  };

  // Get conversation info from navigation state (passed during initial navigation)
  const navigationConversationInfo = location.state?.conversationInfo;

  // Try to find conversation in list, with multiple fallbacks
  const activeConversation = (() => {
    // 1. Try to find in conversations list
    const fromList = conversations.find(
      (conv) =>
        String(conv.companyId) === String(cId) &&
        String(conv.jobId) === String(jId)
    );
    if (fromList) return fromList;

    // 2. Try currentConversationInfo (set by Chat.jsx after init API)
    if (currentConversationInfo &&
        String(currentConversationInfo.companyId) === String(cId) &&
        String(currentConversationInfo.jobId) === String(jId)) {
      return currentConversationInfo;
    }

    // 3. Try navigation state (passed during initial navigation from /chat)
    if (navigationConversationInfo &&
        String(navigationConversationInfo.companyId) === String(cId) &&
        String(navigationConversationInfo.jobId) === String(jId)) {
      return navigationConversationInfo;
    }

    return null;
  })();

  if (loading) return <Loading />;

  return (
    <div className="flex h-[calc(var(--vh,1vh)*100)] font-sans">
      <AnimatePresence>
        {isSidebarOpen && (
          <motion.div
            key="sidebar"
            initial={{ x: "-100%", width: "0%" }}
            animate={{
              x: 0,
              width: isMobile ? "300px" : "30%", // Adjust width based on device
            }}
            exit={{ x: "-100%", width: "0%" }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className="fixed inset-y-0 left-0 z-30 bg-white border-r overflow-y-auto md:relative md:translate-x-0"
          >
            <Sidebar
              conversations={conversations}
              isSidebarOpen={isSidebarOpen}
              setIsSidebarOpen={setIsSidebarOpen}
              changeConversation={changeConversation}
              cId={cId}
              jId={jId}
            />
          </motion.div>
        )}

        {isSidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.3 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#000] z-20 md:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      <div className="flex flex-col w-full">
        <Header
          activeConversation={activeConversation}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />
        <div className="w-full bg-gray-50 h-[90%] md:h-[92%]">
          {React.cloneElement(children, { isSidebarOpen })}
        </div>
      </div>
    </div>
  );
};
